import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getClientId } from "../config/domain";
import { firestore } from "./firebase";

export const sendWordRequest = async (petitionContent, requestId) => {
  try {
    await addDoc(collection(firestore, "word-requests"), {
      petitionContent,
      requestId,
      status: "CREATED",
      createdAt: serverTimestamp(),
    });
    console.log("Word request sent successfully", { requestId });
  } catch (error) {
    console.error("Error sending word request", error);
  }
};
// Function to send log to the endpoint
const sendDocumentLog = async (documentData, documentId) => {
  try {
    const logData = {
      user_email: documentData.user_email || documentData.userEmail || "",
      domain: documentData.domain || "",
      type: documentData.type || "",
      subtype: documentData.subtype || "",
      petition_type:
        documentData.petition_type || documentData.petitionType || "",
      timestamp: new Date().toISOString(),
      document_id: documentId,
      request_id: documentData.requestId || "",
      event_name: "document_created",
    };

    await fetch(
      "https://europe-west10-arbitration-app-456816.cloudfunctions.net/log",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(logData),
      }
    );

    console.log("Document creation log sent successfully", { documentId });
  } catch (error) {
    console.error("Error sending document creation log:", error);
  }
};

// Function to send download log to the endpoint
export const sendDownloadLog = async (
  email,
  petitionType,
  fileType,
  filename,
  requestId
) => {
  try {
    const logData = {
      email: email || "",
      domain: window.location.hostname,
      petition_type: petitionType || "",
      timestamp: new Date().toISOString(),
      fileType: fileType, // 'docx' or 'pdf'
      filename: filename || "",
      request_id: requestId || "",
      event_name: "download_result",
    };

    await fetch(
      "https://europe-west10-arbitration-app-456816.cloudfunctions.net/log",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(logData),
      }
    );

    console.log("Download log sent successfully", {
      fileType,
      petitionType,
      filename,
    });
  } catch (error) {
    console.error("Error sending download log:", error);
  }
};

const removeUnnecessaryFields = (documentData) => {
  const tempDocumentData = JSON.parse(JSON.stringify(documentData));
  Object.keys(tempDocumentData).forEach((key) => {
    if (Array.isArray(tempDocumentData[key])) {
      tempDocumentData[key].forEach((item) => {
        delete item.createdAt;
        delete item.domain;
        delete item.extension;
        delete item.name;
        delete item.petition_type;
        delete item.requestId;
        delete item.selectedTemplateId;
        delete item.sourceId;
        delete item.subtype;
        delete item.type;
        delete item.user_email;
        delete item.rawText;
        delete item.status;
      });
    }
  });
  delete tempDocumentData.createdAt;
  delete tempDocumentData.documentType;
  delete tempDocumentData.thirdPartyPayments;
  delete tempDocumentData.depreciationPayments;
  delete tempDocumentData.damagePayments;
  return tempDocumentData;
};

// Function to upsert document data to requests/{requestId} document
export const upsertRequestDocument = async (
  requestId,
  documentData,
  documentOrder,
  templateId,
  userEmail
) => {
  try {
    if (!requestId) {
      throw new Error("RequestId is required");
    }
    console.log("documentData", documentData);
    const requestDocData = {
      documentData: removeUnnecessaryFields(documentData) || {},
      documentOrder: documentOrder || [],
      templateId: templateId || "",
      lastUpdated: serverTimestamp(),
      status: "COMPLETED",
    };

    const clientId = getClientId();
    const requestDocRef = doc(
      firestore,
      "requests",
      clientId,
      "requests",
      requestId
    );
    // Use setDoc with merge: true for upsert behavior
    await setDoc(requestDocRef, requestDocData, { merge: true });

    console.log("Document data upserted to request document:", requestId);
    return requestId;
  } catch (error) {
    console.error("Error upserting request document:", error);
    throw error;
  }
};

export const createDocument = async (data) => {
  try {
    console.log("creating document", data);
    const clientId = getClientId();
    const documentsCollection = collection(
      firestore,
      "documents",
      clientId,
      "documents"
    );
    const docRef = await addDoc(documentsCollection, {
      ...data,
      createdAt: serverTimestamp(),
    });

    console.log("Document created with ID:", docRef.id);

    // Send log after successful document creation
    sendDocumentLog(data, docRef.id);

    return docRef.id;
  } catch (error) {
    console.error("Error creating document:", error);
    throw error;
  }
};

export const subscribeToDocument = (documentId, callback) => {
  const clientId = getClientId();
  return onSnapshot(
    doc(firestore, "documents", clientId, "documents", documentId),
    (doc) => {
      if (doc.exists()) {
        callback(doc.data());
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("Error listening to document:", error);
    }
  );
};

// Function to query requests collection by requestId
export const getRequestDocumentData = async (requestId) => {
  try {
    if (!requestId) {
      throw new Error("RequestId is required");
    }

    const clientId = getClientId();
    const requestDocRef = doc(
      firestore,
      "requests",
      clientId,
      "requests",
      requestId
    );
    const requestDocSnap = await getDoc(requestDocRef);

    if (requestDocSnap.exists()) {
      const requestData = requestDocSnap.data();
      console.log("Request document data:", requestData);
      (requestData.documentData.damage_receipt || []).forEach(
        (d) => (d.status = "DONE")
      );
      (requestData.documentData.third_party_receipt || []).forEach(
        (d) => (d.status = "DONE")
      );
      (requestData.documentData.recourse_receipt || []).forEach(
        (d) => (d.status = "DONE")
      );
      (requestData.documentData.depreciation_receipt || []).forEach(
        (d) => (d.status = "DONE")
      );
      (requestData.documentData.additional_document || []).forEach(
        (d) => (d.status = "DONE")
      );
      return requestData;
    } else {
      console.log("No request document found with ID:", requestId);
      return null;
    }
  } catch (error) {
    console.error("Error querying request document:", error);
    return null;
  }
};
